/**
 * SideBySideReviewPlayer — Animator-facing read-only SyncSketch view
 * ==================================================================
 * Founder spec (Feb 2026, "My Approvals" Phase 1B):
 * When the artist's primary skill is **animator** (or layout_artist),
 * a plain text/pin list is not enough — they need the SyncSketch-style
 * side-by-side player so they can:
 *   • SEE the CBA animatic next to their layout playblast
 *   • SEE supervisor + AI pins overlaid spatially on their playblast
 *   • CLICK a pin → both videos seek to that timecode in sync
 *
 * This is the READ-ONLY twin of `CompareView.js`. We intentionally
 * built a fresh component instead of threading `readOnly={true}` into
 * the 1000+ line CompareView, because:
 *   • CompareView has dozens of reviewer-only widgets (approve buttons,
 *     decision pills, pin-edit toolbars, send-to-client, etc.) — they'd
 *     all need conditional render branches.
 *   • This player is intentionally minimal — just two videos + pin
 *     overlay + sync controls — which keeps the artist's UI calm and
 *     focused on "understanding the critique" not "navigating the tool".
 *
 * Pins ship in normalized (0..1) coordinates from review_annotations,
 * so we render them as absolutely-positioned dots on top of the right-
 * side video, scaled to whatever rendered size the video is at.
 *
 * Phase 3 (AI Layout Supervisor) will write pins with `author_role`
 * starting with `ai_` — they get a distinct purple Bot badge so artists
 * can mentally separate "AI said this" from "human supervisor said this"
 * even though both are equally actionable.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Play, Pause, RotateCcw, Volume2, VolumeX, ChevronLeft, ChevronRight,
  X, MessageSquare, Star, User, Loader2, Film, Maximize2, Eye,
} from 'lucide-react';

function _formatTc(secs) {
  if (secs == null || isNaN(secs)) return '00:00';
  const s = Math.floor(secs);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function _resolveMediaUrl(media) {
  if (!media || media.length === 0) return null;
  for (const m of media) {
    const url = typeof m === 'string' ? m : (m.url || m.file_url || '');
    if (!url) continue;
    if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return url;
  }
  // Fallback — first item regardless of extension
  const first = media[0];
  return typeof first === 'string' ? first : (first.url || first.file_url || null);
}

export default function SideBySideReviewPlayer({ detail, onClose }) {
  // ── Refs to both <video> elements ──
  const leftRef = useRef(null);     // CBA animatic (reference)
  const rightRef = useRef(null);    // Animator's playblast (the one being reviewed)
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [activePin, setActivePin] = useState(null);
  const [currentT, setCurrentT] = useState(0);
  const [duration, setDuration] = useState(0);

  const { submission, events, annotations, cba_reference, task_context } = detail;

  const cbaUrl = cba_reference?.video_url || null;
  const myUrl = useMemo(
    () => _resolveMediaUrl(submission?.media || submission?.file_urls),
    [submission]);

  // Annotation pins sorted by timecode — used both for the dot overlay
  // AND the right-side scrolling notes panel
  const pinsSorted = useMemo(() => [...(annotations || [])].sort((a, b) => {
    const ta = a.timecode_sec ?? 999999;
    const tb = b.timecode_sec ?? 999999;
    return ta - tb;
  }), [annotations]);

  // ── Master play/pause — both videos start together ──
  // Founder ask Feb 2026: "both > CBA & layout will at the same time".
  // We await BOTH play() promises in parallel (Promise.allSettled so one
  // failing — e.g. autoplay blocked on a video that needs user gesture —
  // doesn't reject the other). This is the only reliable way to start
  // two <video> elements in lock-step in the browser; calling play()
  // sequentially can drift up to 80ms on slow-decode systems.
  const togglePlay = async () => {
    const wantPlay = !playing;
    setPlaying(wantPlay);
    if (wantPlay) {
      // Re-sync the slave video to the master clock FIRST so both start
      // from the exact same timestamp.
      const masterT = rightRef.current?.currentTime ?? 0;
      if (leftRef.current && Math.abs(leftRef.current.currentTime - masterT) > 0.1) {
        try { leftRef.current.currentTime = masterT; } catch (_e) { /* media not seekable yet */ }
      }
      await Promise.allSettled([
        leftRef.current?.play(),
        rightRef.current?.play(),
      ]);
    } else {
      [leftRef, rightRef].forEach(ref => ref.current?.pause());
    }
  };

  const syncBoth = (t) => {
    setCurrentT(t);
    [leftRef, rightRef].forEach(ref => {
      if (!ref.current) return;
      const target = Math.min(t, ref.current.duration || t);
      // Avoid jitter — only seek if delta > 100ms
      if (Math.abs((ref.current.currentTime || 0) - target) > 0.1) {
        try { ref.current.currentTime = target; } catch (_e) { /* media not seekable yet */ }
      }
    });
  };

  const restart = () => {
    syncBoth(0);
    [leftRef, rightRef].forEach(ref => ref.current?.pause());
    setPlaying(false);
  };

  const toggleMute = () => {
    const want = !muted;
    setMuted(want);
    if (leftRef.current)  leftRef.current.muted  = want;
    if (rightRef.current) rightRef.current.muted = want;
  };

  // ── Pin navigation (used by [ ] hotkeys + 1-9 direct jump) ──
  const jumpToPin = (pin) => {
    if (!pin) return;
    setActivePin(pin.id);
    if (pin.timecode_sec != null) syncBoth(pin.timecode_sec);
  };
  const jumpToPinIndex = (idx) => {
    if (idx < 0 || idx >= pinsSorted.length) return;
    jumpToPin(pinsSorted[idx]);
  };
  const jumpPrevPin = () => {
    if (!pinsSorted.length) return;
    // Find the pin with the latest timecode strictly less than current time.
    const beforeNow = pinsSorted.filter(p => (p.timecode_sec ?? -1) < currentT - 0.3);
    if (beforeNow.length === 0) return jumpToPin(pinsSorted[pinsSorted.length - 1]);
    jumpToPin(beforeNow[beforeNow.length - 1]);
  };
  const jumpNextPin = () => {
    if (!pinsSorted.length) return;
    const afterNow = pinsSorted.find(p => (p.timecode_sec ?? 999999) > currentT + 0.3);
    jumpToPin(afterNow || pinsSorted[0]);
  };

  // Time-update from RIGHT video drives the master clock + scrubber
  // (right is the animator's playblast — the artifact being reviewed,
  // so its full length is what we track).
  useEffect(() => {
    const v = rightRef.current;
    if (!v) return;
    const onTime = () => setCurrentT(v.currentTime || 0);
    const onLoad = () => setDuration(v.duration || 0);
    const onEnded = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onLoad);
    v.addEventListener('ended', onEnded);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onLoad);
      v.removeEventListener('ended', onEnded);
    };
  }, [myUrl]);

  // ── Keyboard shortcuts (industry standard JKL + SyncSketch-style pin nav) ──
  // Founder ask Feb 2026: animators use this player daily — give them the
  // muscle-memory shortcuts they already know from SyncSketch / Flow /
  // Premiere review tools.
  //   Space  → play/pause (master, both videos in sync)
  //   J      → step back 2s in both videos
  //   K      → pause (industry-standard JKL "stop")
  //   L      → step forward 2s in both videos
  //   [ / ]  → previous / next pin (relative to current playhead)
  //   1-9    → jump directly to the Nth pin in the timeline
  //   M      → toggle mute on both videos
  //   .      → restart (both videos back to 00:00)
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in an input/textarea
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

      const key = e.key;
      let handled = true;
      switch (key) {
        case ' ':
        case 'Spacebar':
          togglePlay();
          break;
        case 'j': case 'J':
          syncBoth(Math.max(0, currentT - 2));
          break;
        case 'k': case 'K':
          if (playing) togglePlay();
          break;
        case 'l': case 'L':
          syncBoth(Math.min(duration || currentT + 2, currentT + 2));
          break;
        case '[':
          jumpPrevPin();
          break;
        case ']':
          jumpNextPin();
          break;
        case 'm': case 'M':
          toggleMute();
          break;
        case '.':
          restart();
          break;
        case '1': case '2': case '3': case '4': case '5':
        case '6': case '7': case '8': case '9':
          jumpToPinIndex(parseInt(key, 10) - 1);
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentT, duration, playing, muted, pinsSorted]);

  // Pin click → seek both videos + activate pin
  const onPinClick = (pin) => jumpToPin(pin);

  // Render pin dots over the right video. Each pin has normalized
  // (x_pct, y_pct) or (x, y) in 0..1 plus a timecode.
  const renderPinOverlay = () => {
    if (!pinsSorted.length) return null;
    // Show only pins within 1.5s of current time (active markers)
    return (
      <div className="absolute inset-0 pointer-events-none">
        {pinsSorted.map((pin, i) => {
          const tc = pin.timecode_sec ?? 0;
          const near = Math.abs(tc - currentT) < 1.5;
          if (!near) return null;
          const x = (pin.x_pct ?? pin.x ?? 0.5) * 100;
          const y = (pin.y_pct ?? pin.y ?? 0.5) * 100;
          const isAI = (pin.author_role || '').toLowerCase().includes('ai');
          const isActive = activePin === pin.id;
          return (
            <div
              key={pin.id || i}
              data-testid={`sbs-pin-dot-${i}`}
              className={`absolute pointer-events-auto rounded-full border-2 cursor-pointer transition-all ${
                isActive ? 'w-7 h-7 ring-2 ring-amber-300/60' : 'w-5 h-5'
              } ${isAI
                ? 'bg-amber-600/80 border-amber-200'
                : 'bg-amber-500/80 border-amber-200'} hover:scale-110`}
              style={{
                left: `calc(${x}% - 10px)`,
                top: `calc(${y}% - 10px)`,
                animation: near ? 'pulse 1.6s ease-in-out infinite' : '',
              }}
              onClick={(e) => { e.stopPropagation(); onPinClick(pin); }}
              title={pin.text || ''}
            >
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] font-bold text-white whitespace-nowrap bg-black/60 px-1 rounded">
                {i + 1}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Scrubber timeline with pin tick marks
  const renderScrubber = () => {
    const pct = duration > 0 ? (currentT / duration) * 100 : 0;
    return (
      <div className="relative">
        <input
          type="range"
          min={0}
          max={duration || 1}
          step="0.05"
          value={currentT}
          onChange={(e) => syncBoth(parseFloat(e.target.value))}
          className="w-full h-1.5 rounded-full appearance-none bg-zinc-800 accent-amber-400"
          data-testid="sbs-scrubber"
        />
        {/* Pin tick marks */}
        <div className="absolute inset-x-0 top-0 h-1.5 pointer-events-none">
          {pinsSorted.map((pin, i) => {
            if (pin.timecode_sec == null || duration === 0) return null;
            const left = (pin.timecode_sec / duration) * 100;
            const isAI = (pin.author_role || '').toLowerCase().includes('ai');
            return (
              <div
                key={pin.id || i}
                className={`absolute top-0 w-0.5 h-1.5 ${isAI ? 'bg-amber-300' : 'bg-amber-400'}`}
                style={{ left: `${left}%` }}
                title={pin.text}
              />
            );
          })}
          {/* Playhead */}
          <div
            className="absolute top-0 w-0.5 h-2 bg-white"
            style={{ left: `${pct}%`, transform: 'translateX(-1px) translateY(-1px)' }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-stretch overflow-hidden"
      data-testid="sbs-review-modal"
    >
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-950 flex items-center gap-3">
          <button
            onClick={onClose}
            data-testid="sbs-close"
            className="text-zinc-400 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-white truncate">
                {task_context?.title || submission?.title || 'Review'}
              </h2>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/15 text-blue-300 border border-blue-500/40">
                R{submission?.round || 1}
              </span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                READ-ONLY
              </span>
            </div>
            <p className="text-[10px] text-zinc-500 truncate">
              {task_context?.project_name}
              {task_context?.shot_code && (
                <> · <span className="font-mono">{task_context.shot_code}</span></>
              )}
              {cba_reference?.canonical_name && (
                <> · ref {cba_reference.canonical_name}</>
              )}
            </p>
          </div>
          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {pinsSorted.length} notes
          </span>
        </div>

        {/* Video grid */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-3 bg-black p-3">
          {/* LEFT — CBA animatic */}
          <div className="relative bg-black flex flex-col rounded-lg overflow-hidden border-2 border-cyan-500/50 shadow-[0_0_30px_rgba(34,211,238,0.15)]">
            <div className="px-3 py-1.5 bg-cyan-500/10 border-b border-cyan-500/40 flex items-center gap-2 shrink-0">
              <Film className="w-3 h-3 text-cyan-400" />
              <span className="text-[10px] font-bold text-cyan-300 uppercase tracking-wider">
                Reference (CBA Animatic)
              </span>
              <span className="text-[10px] text-cyan-200/70 font-mono ml-auto truncate">
                {cba_reference?.canonical_name || (cbaUrl ? '' : 'No CBA linked')}
              </span>
            </div>
            <div className="flex-1 flex items-center justify-center min-h-0 bg-black">
              {cbaUrl ? (
                <video
                  ref={leftRef}
                  src={cbaUrl}
                  className="max-w-full max-h-full"
                  data-testid="sbs-cba-video"
                  preload="metadata"
                  playsInline
                />
              ) : (
                <div className="text-center p-6">
                  <Eye className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                  <p className="text-xs text-zinc-500">
                    No CBA animatic linked to this task.
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-1">
                    Ask your PM to attach the reference clip if you need one.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Animator's playblast with pin overlay */}
          <div className="relative bg-black flex flex-col rounded-lg overflow-hidden border-2 border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
            <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/40 flex items-center gap-2 shrink-0">
              <Film className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] font-bold text-amber-300 uppercase tracking-wider">
                Your Submission
              </span>
              <span className="text-[10px] text-amber-200/70 font-mono ml-auto truncate">
                R{submission?.round || 1}
              </span>
            </div>
            <div className="flex-1 flex items-center justify-center min-h-0 bg-black relative">
              {myUrl ? (
                <>
                  <video
                    ref={rightRef}
                    src={myUrl}
                    className="max-w-full max-h-full"
                    data-testid="sbs-my-video"
                    preload="metadata"
                    playsInline
                  />
                  {/* Pin dots overlay on top of the playblast */}
                  {renderPinOverlay()}
                </>
              ) : (
                <div className="text-center p-6">
                  <Loader2 className="w-8 h-8 mx-auto text-zinc-700 mb-2 animate-spin" />
                  <p className="text-xs text-zinc-500">No media available.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transport controls */}
        <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-950 flex items-center gap-3">
          <button
            onClick={togglePlay}
            data-testid="sbs-play-pause"
            className="w-9 h-9 bg-amber-400 hover:bg-amber-300 text-zinc-950 rounded-full flex items-center justify-center"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <button
            onClick={restart}
            data-testid="sbs-restart"
            className="text-zinc-400 hover:text-white p-1"
            aria-label="Restart"
            title="Restart from 00:00"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={toggleMute}
            data-testid="sbs-mute"
            className="text-zinc-400 hover:text-white p-1"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <span className="text-[10px] text-zinc-500 font-mono tabular-nums shrink-0">
            {_formatTc(currentT)} / {_formatTc(duration)}
          </span>
          <div className="flex-1 min-w-0 px-2">
            {renderScrubber()}
          </div>
          <span className="text-[10px] text-zinc-500 hidden lg:flex items-center gap-1.5 font-mono">
            <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">Space</kbd> play
            <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">J</kbd><kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">K</kbd><kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">L</kbd> shuttle
            <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">[</kbd><kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">]</kbd> pins
            <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">1-9</kbd> jump
          </span>
        </div>
      </div>

      {/* Notes side panel */}
      <aside className="w-96 max-w-[40vw] border-l border-zinc-800 bg-zinc-950 overflow-y-auto"
             data-testid="sbs-notes-panel">
        <div className="px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <h3 className="text-xs font-bold text-white">Critique Timeline</h3>
          <p className="text-[10px] text-zinc-500">
            Click any note to seek both videos to that frame.
          </p>
        </div>

        {/* Producer events (decisions per round) */}
        {events && events.length > 0 && (
          <section className="px-4 py-3 border-b border-zinc-800/60">
            <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
              Decisions
            </h4>
            {events.map((ev, i) => {
              const a = (ev.action || '').toLowerCase();
              const isAI = a.startsWith('ai_');
              const Ph = isAI ? Star : User;
              return (
                <div key={i}
                     data-testid={`sbs-event-${i}`}
                     className={`p-2 rounded mb-1.5 border ${
                       isAI ? 'border-amber-500/40 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/50'
                     }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Ph className={`w-3 h-3 ${isAI ? 'text-amber-300' : 'text-zinc-400'}`} />
                    <span className={`text-[10px] font-bold ${isAI ? 'text-amber-200' : 'text-zinc-300'}`}>
                      {ev.actor_name || (isAI ? 'Supervisor' : 'Reviewer')}
                    </span>
                    <span className="text-[9px] text-zinc-500 ml-auto">
                      {a.replace(/^(supervisor_|pm_|internal_|ai_)/, '').toUpperCase()}
                    </span>
                  </div>
                  {ev.note && (
                    <p className="text-[11px] text-zinc-200 whitespace-pre-wrap">{ev.note}</p>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* Frame-specific pins */}
        <section className="px-4 py-3">
          <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            Frame Notes ({pinsSorted.length})
          </h4>
          {pinsSorted.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">
              No frame-specific notes were left.
            </p>
          ) : (
            <div className="space-y-1.5">
              {pinsSorted.map((pin, i) => {
                const tc = _formatTc(pin.timecode_sec);
                const isAI = (pin.author_role || '').toLowerCase().includes('ai');
                const isActive = activePin === pin.id;
                return (
                  <button
                    key={pin.id || i}
                    data-testid={`sbs-pin-${i}`}
                    onClick={() => onPinClick(pin)}
                    className={`w-full text-left p-2 rounded border transition ${
                      isActive
                        ? 'border-amber-400 bg-amber-500/10'
                        : isAI
                          ? 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10'
                          : 'border-zinc-800 bg-zinc-900/40 hover:bg-zinc-800'}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-bold bg-zinc-800 text-zinc-300 rounded px-1">
                          {i + 1}
                        </span>
                        {isAI && <Star className="w-3 h-3 text-amber-300" />}
                        <span className="font-mono text-[10px] text-amber-300 bg-amber-500/10 px-1 rounded">
                          @{tc}
                        </span>
                      </div>
                      <span className={`text-[10px] ${isAI ? 'text-amber-200' : 'text-zinc-300'}`}>
                        {pin.author_name || (isAI ? 'Supervisor' : 'Reviewer')}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-200 whitespace-pre-wrap leading-snug">
                      {pin.text || <em className="text-zinc-500">(no text)</em>}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </aside>
    </div>
  );
}
